/**
 * UnexecutedTransferModal Component
 * Modal shown during report submission when there are unreported transfer bonds (UT powers).
 * Allows user to select which transfer bonds to add to the current report before continuing.
 * This matches the legacy TransferBondsExecutingKnown.html dialog behavior.
 */

import { useState, useEffect } from 'react';
import { ArrowsRightLeftIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { formatCurrencyWithSymbol } from '../utils/formatters';

// Hoisted default to avoid creating new array reference on each render (rerender-memo-with-default-value)
const EMPTY_ARRAY = [];

// Skeleton row component for loading state (desktop table)
const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-3 py-3"><div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded" /></td>
    <td className="px-3 py-3"><div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" /></td>
    <td className="px-3 py-3"><div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" /></td>
    <td className="px-3 py-3"><div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" /></td>
    <td className="px-3 py-3"><div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" /></td>
  </tr>
);

// Skeleton card for loading state (mobile)
const SkeletonCard = () => (
  <div className="rounded-lg border-2 border-gray-200 dark:border-gray-700 p-3 animate-pulse">
    <div className="flex items-center gap-3">
      <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
      <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
    </div>
  </div>
);

const UnexecutedTransferModal = ({
  isOpen,
  onClose,
  transferPowers = EMPTY_ARRAY,
  onContinue,
  onAddTransfers,
  isAdding = false,
  isLoading = false,
  error = null,
  onClearError
}) => {
  // Track which powers are selected (default none selected - user must manually select)
  const [selectedPowers, setSelectedPowers] = useState(() => new Set());

  // Reset selections when modal opens (start with none selected)
  useEffect(() => {
    if (isOpen) {
      setSelectedPowers(new Set());
    }
  }, [isOpen]);

  // Clear error when modal closes
  useEffect(() => {
    if (!isOpen && error && onClearError) {
      onClearError();
    }
  }, [isOpen, error, onClearError]);

  const handleTogglePower = (powerId) => {
    setSelectedPowers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(powerId)) {
        newSet.delete(powerId);
      } else {
        newSet.add(powerId);
      }
      return newSet;
    });
  };

  const allSelected = transferPowers.length > 0 && transferPowers.every(p => selectedPowers.has(p.powerId || p.powerNumber));

  const handleSelectAllToggle = (checked) => {
    if (checked) {
      setSelectedPowers(new Set(transferPowers.map(p => p.powerId || p.powerNumber)));
    } else {
      setSelectedPowers(new Set());
    }
  };

  const handleAddAndContinue = async () => {
    // Clear any previous error
    if (onClearError) onClearError();

    if (selectedPowers.size === 0) {
      // No transfers selected, just continue
      onContinue();
      return;
    }

    // Get the selected power objects
    const powersToAdd = transferPowers.filter(p =>
      selectedPowers.has(p.powerId || p.powerNumber)
    );

    // Let the parent component handle adding and continuing
    // Parent's onAddTransfers will call onContinue on success
    await onAddTransfers(powersToAdd);
  };

  const handleSkip = () => {
    onContinue();
  };

  // Show modal in loading state if isLoading, or if we have data
  if (!isOpen || (!isLoading && transferPowers.length === 0)) return null;

  return (
    <OathModal
      isOpen={isOpen}
      onClose={isAdding ? undefined : onClose}
      title="Unreported Transfer Bonds"
      size="2xl"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={onClose}
            disabled={isAdding || isLoading}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            variant="outline"
            onClick={handleSkip}
            disabled={isAdding || isLoading}
            className="w-full sm:w-auto"
          >
            Skip
          </OathButton>
          <OathButton
            onClick={handleAddAndContinue}
            disabled={isAdding || isLoading}
            className="w-full sm:w-auto"
          >
            {isAdding ? (
              <span className="flex items-center gap-2">
                <OathSpinner size="sm" />
                Adding...
              </span>
            ) : isLoading ? (
              <span className="flex items-center gap-2">
                <OathSpinner size="sm" />
                Loading...
              </span>
            ) : selectedPowers.size > 0 ? (
              `Add ${selectedPowers.size} & Continue`
            ) : (
              'Continue'
            )}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-3 sm:space-y-4">
        {/* Error message */}
        {error && (
          <OathAlert variant="error" onDismiss={onClearError}>
            <div className="flex items-start gap-2">
              <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Failed to add transfer powers</p>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
          </OathAlert>
        )}

        {/* Header context — restyled to match the cohesive card look */}
        <div className="rounded-2xl bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 p-3 sm:p-4">
          <div className="flex items-start gap-3">
            <ArrowsRightLeftIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                There are unreported Transfer Bonds.
              </p>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                Add them to this report now, or skip to continue without them.
              </p>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {isLoading ? (
          <>
            {/* Mobile skeleton cards */}
            <div className="sm:hidden space-y-2">
              {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
            </div>

            {/* Desktop skeleton table */}
            <div className="hidden sm:block border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Add</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Power</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Amount</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">First Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Last Name</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {[1, 2, 3].map((i) => <SkeletonRow key={i} />)}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            {/* Mobile cards — stacked, touch-friendly */}
            <div className="sm:hidden border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* Select-all header */}
              {transferPowers.length > 0 && (
                <label className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => handleSelectAllToggle(e.target.checked)}
                    disabled={isAdding}
                    className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select all <span className="text-gray-500 dark:text-gray-400">({transferPowers.length})</span>
                  </span>
                </label>
              )}

              <div className="p-2 space-y-2">
                {transferPowers.map((power) => {
                  const powerId = power.powerId || power.powerNumber;
                  const isSelected = selectedPowers.has(powerId);
                  const firstName = power.defendantFirstName ||
                    power.powerReportDefendant?.firstName ||
                    (power.defendantName ? power.defendantName.split(' ')[0] : '');
                  const lastName = power.defendantLastName ||
                    power.powerReportDefendant?.lastName ||
                    (power.defendantName ? power.defendantName.split(' ').slice(1).join(' ') : '');
                  const fullName = [firstName, lastName].filter(Boolean).join(' ') || '—';

                  return (
                    <div
                      key={powerId}
                      onClick={() => !isAdding && handleTogglePower(powerId)}
                      className={`relative rounded-lg border-2 p-3 cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary-50/50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                      } ${isAdding ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleTogglePower(powerId)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isAdding}
                          className="h-5 w-5 mt-0.5 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-mono font-semibold text-sm text-gray-900 dark:text-white">
                            {power.powerNumber || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5" title={fullName}>
                            {fullName}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Amount</p>
                          <p className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                            {formatCurrencyWithSymbol(power.powerAmount || power.bondAmount || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-80 overflow-y-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => handleSelectAllToggle(e.target.checked)}
                        disabled={isAdding}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Power</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">First Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Last Name</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {transferPowers.map((power) => {
                    const powerId = power.powerId || power.powerNumber;
                    const isSelected = selectedPowers.has(powerId);
                    const firstName = power.defendantFirstName ||
                      power.powerReportDefendant?.firstName ||
                      (power.defendantName ? power.defendantName.split(' ')[0] : '');
                    const lastName = power.defendantLastName ||
                      power.powerReportDefendant?.lastName ||
                      (power.defendantName ? power.defendantName.split(' ').slice(1).join(' ') : '');

                    return (
                      <tr
                        key={powerId}
                        onClick={() => !isAdding && handleTogglePower(powerId)}
                        className={`cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-primary-50/50 dark:bg-primary-900/20'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        } ${isAdding ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleTogglePower(powerId)}
                            disabled={isAdding}
                            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700"
                          />
                        </td>
                        <td className="px-3 py-3">
                          <span className="font-mono font-medium text-gray-900 dark:text-white">
                            {power.powerNumber || 'N/A'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right text-gray-900 dark:text-white tabular-nums">
                          {formatCurrencyWithSymbol(power.powerAmount || power.bondAmount || 0)}
                        </td>
                        <td className="px-3 py-3 text-gray-700 dark:text-gray-300">
                          {firstName || '-'}
                        </td>
                        <td className="px-3 py-3 text-gray-700 dark:text-gray-300">
                          {lastName || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Selection counter */}
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {selectedPowers.size} of {transferPowers.length} transfer bond{transferPowers.length !== 1 ? 's' : ''} selected
            </p>
          </>
        )}
      </div>
    </OathModal>
  );
};

export default UnexecutedTransferModal;
